import { TFunction } from 'i18next';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import { InvalidCodeReasons, SoCodeTypes } from '../../../../enums';
import { CodeModel } from '../../../../models/codes';
import { resources } from '../../../../resources/resources';

export enum CodeSources {
  Generated = 0,
  ManuallyAdded = 1,
}

export enum CodeDialogTypes {
  Generate = 0,
  ManuallyAdded = 1,
  Preview = 2,
}

export const formatCode = (code: CodeModel): string => {
  if (code.type === SoCodeTypes.Default) return code.code;
  return `${code.code} PIN ${code.pin}`;
};

export const ValidCodes = (props: { codes: CodeModel[] }) => {
  const { codes } = props;
  const { t: translate } = useTranslation();
  return (
    <Notification
      type={NotificationType.Success}
      scrollUp={false}
      messages={[
        {
          message: translate(resources.codes.manual.valid, {
            0: codes.length,
          }),
        },
      ]}
    />
  );
};

export const InvalidCodes = (props: { codes: CodeModel[] }) => {
  const { codes } = props;
  const { t: translate } = useTranslation();

  if (!codes.length) return null;

  const groupedCodes: { [k in InvalidCodeReasons]: string[] } = codes
    .filter(e => !_.isNil(e.invalidReason))
    .reduce(
      (acc, curr) => {
        if (_.isNil(acc[curr.invalidReason!])) {
          acc[curr.invalidReason!] = [formatCode(curr)];
        } else {
          acc[curr.invalidReason!].push(formatCode(curr));
        }
        return acc;
      },
      {} as { [k in InvalidCodeReasons]: string[] },
    );

  const messages = [
    {
      message: `<b>${translate(resources.codes.manual.invalid.manual, {
        0: codes.length,
      })}</b>`,
    },
  ];
  if (groupedCodes[InvalidCodeReasons.AlreadyExisting]?.length) {
    messages.push({
      message: getCodeErrorMessage(
        InvalidCodeReasons.AlreadyExisting,
        groupedCodes[InvalidCodeReasons.AlreadyExisting],
        translate,
      ),
    });
  }
  if (groupedCodes[InvalidCodeReasons.CodeMaxLength]?.length) {
    messages.push({
      message: getCodeErrorMessage(
        InvalidCodeReasons.CodeMaxLength,
        groupedCodes[InvalidCodeReasons.CodeMaxLength],
        translate,
      ),
    });
  }
  if (groupedCodes[InvalidCodeReasons.PinMaxLength]?.length) {
    messages.push({
      message: getCodeErrorMessage(
        InvalidCodeReasons.PinMaxLength,
        groupedCodes[InvalidCodeReasons.PinMaxLength],
        translate,
      ),
    });
  }
  if (groupedCodes[InvalidCodeReasons.EmptyCode]?.length) {
    messages.push({
      message: getCodeErrorMessage(
        InvalidCodeReasons.EmptyCode,
        groupedCodes[InvalidCodeReasons.EmptyCode],
        translate,
      ),
    });
  }
  if (groupedCodes[InvalidCodeReasons.EmptyPin]?.length) {
    messages.push({
      message: getCodeErrorMessage(
        InvalidCodeReasons.EmptyPin,
        groupedCodes[InvalidCodeReasons.EmptyPin],
        translate,
      ),
    });
  }
  return (
    <Notification
      type={NotificationType.Error}
      scrollUp={false}
      messages={messages}
    />
  );
};

const getCodeErrorMessage = (
  reason: InvalidCodeReasons,
  codes: string[],
  translate: TFunction<'translate'>,
): string => {
  const formatMessage = (error: string, info: string, codes: string) =>
    `<b>${error}</b> <span>${info}</span><p>${codes}</p>`;

  const codesErrorMessage = translate(resources.codes.manual.invalid.error, {
    0: codes.join(', '),
  });

  if (reason === InvalidCodeReasons.AlreadyExisting)
    return formatMessage(
      translate(resources.codes.manual.invalid.existingCode.error),
      translate(resources.codes.manual.invalid.existingCode.info),
      codesErrorMessage,
    );
  if (reason === InvalidCodeReasons.CodeMaxLength)
    return formatMessage(
      translate(resources.codes.manual.invalid.codeMaxLength.error),
      translate(resources.codes.manual.invalid.codeMaxLength.info),
      codesErrorMessage,
    );
  if (reason === InvalidCodeReasons.PinMaxLength)
    return formatMessage(
      translate(resources.codes.manual.invalid.pinMaxLength.error),
      translate(resources.codes.manual.invalid.pinMaxLength.info),
      codesErrorMessage,
    );
  if (reason === InvalidCodeReasons.EmptyCode)
    return formatMessage(
      translate(resources.codes.manual.invalid.emptyCode.error),
      translate(resources.codes.manual.invalid.emptyCode.info),
      codesErrorMessage,
    );
  if (reason === InvalidCodeReasons.EmptyPin)
    return formatMessage(
      translate(resources.codes.manual.invalid.emptyPin.error),
      translate(resources.codes.manual.invalid.emptyPin.info),
      codesErrorMessage,
    );
  return '';
};
